import { useState, useEffect, useCallback } from 'react';
import {
    Box, Card, CardHeader, CardContent, Button, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions,
    MenuItem, IconButton, Tooltip,
} from '@mui/material';
import { DataGrid, itIT } from '@mui/x-data-grid';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import FastTextField from '../FastTextField';
import FormikValidation from '../FormikValidation';
import Api from '../../Services/Api';
import { areas as optionsArea } from '../../Config/constants';

const theme = createTheme({ palette: { primary: { main: '#11cb5f' } } }, itIT);

const EMPTY_VALUES = {
    area: '',
    min_power: '',
    max_power: '',
    price: '',
    periodicity_years: '',
};

function StampTypeSection() {
    const [rows, setRows] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saveAlert, setSaveAlert] = useState(0);
    const [listAlert, setListAlert] = useState(0);

    const fetchStampTypes = useCallback(() => {
        Api.get({ endpoint: 'stamp-type', contentType: 'application/json' })
            .then(res => setRows(res.data.stampTypes))
            .catch(() => setListAlert(-1));
    }, []);

    useEffect(() => { fetchStampTypes(); }, [fetchStampTypes]);

    const columns = [
        { field: 'area', headerName: 'Provincia', flex: 1 },
        { field: 'min_power', headerName: 'Potenza min. (kW)', flex: 1, type: 'number' },
        { field: 'max_power', headerName: 'Potenza max. (kW)', flex: 1, type: 'number' },
        {
            field: 'price', headerName: 'Prezzo (€)', flex: 1, type: 'number',
            valueFormatter: (params) => params.value != null ? `${params.value} €` : '-',
        },
        { field: 'periodicity_years', headerName: 'Periodicità (anni)', flex: 1, type: 'number' },
        {
            field: 'actions', headerName: 'Azioni', width: 110, sortable: false,
            renderCell: (params) => (
                <>
                    <Tooltip title="Modifica">
                        <IconButton size="small" onClick={() => openEdit(params.row)}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Elimina">
                        <IconButton size="small" color="error" onClick={() => handleDelete(params.row.id)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </>
            ),
        },
    ];

    function openCreate() {
        setEditingId(null);
        formik.resetForm({ values: EMPTY_VALUES });
        setSaveAlert(0);
        setDialogOpen(true);
    }

    function openEdit(row) {
        setEditingId(row.id);
        formik.resetForm({
            values: {
                area: row.area ?? '',
                min_power: row.min_power ?? '',
                max_power: row.max_power ?? '',
                price: row.price ?? '',
                periodicity_years: row.periodicity_years ?? '',
            },
        });
        setSaveAlert(0);
        setDialogOpen(true);
    }

    function handleDelete(id) {
        if (!window.confirm('Eliminare questo tipo di bollino?')) return;
        Api.remove({ endpoint: `stamp-type/${id}` })
            .then(() => fetchStampTypes())
            .catch(err => {
                if (err.response?.status === 422 && err.response?.data?.error === 1001) {
                    window.alert('Impossibile eliminare: esistono bollini associati a questo tipo.\nEliminare prima i bollini collegati.');
                } else {
                    window.alert('Errore nell\'eliminazione. Riprovare.');
                }
            });
    }

    const formik = useFormik({
        initialValues: EMPTY_VALUES,
        validationSchema: Yup.object({
            area: Yup.string().length(2, 'Lunghezza 2 caratteri').required('Campo obbligatorio'),
            min_power: Yup.number().typeError('Inserire un numero').required('Campo obbligatorio').min(0, 'Valore non valido'),
            max_power: Yup.number().typeError('Inserire un numero').required('Campo obbligatorio')
                .moreThan(Yup.ref('min_power'), 'Deve essere > Potenza min.'),
            price: Yup.number().typeError('Inserire un numero').nullable().min(0, 'Valore non valido'),
            periodicity_years: Yup.number().typeError('Inserire un numero intero').integer('Deve essere intero')
                .required('Campo obbligatorio').min(1, 'Minimo 1'),
        }),
        onSubmit: (values) => {
            setLoading(true);
            setSaveAlert(0);
            const isEdit = Boolean(editingId);
            const body = {
                ...values,
                price: values.price === '' ? null : values.price,
                ...(isEdit ? { _method: 'put' } : {}),
            };
            const endpoint = isEdit ? `stamp-type/${editingId}` : 'stamp-type';
            Api.post({ endpoint, body, contentType: 'application/json' })
                .then(() => {
                    setDialogOpen(false);
                    fetchStampTypes();
                    setSaveAlert(1);
                })
                .catch(err => {
                    if (err.response?.status === 422) {
                        const data = err.response.data;
                        if (data?.errors) {
                            Object.entries(data.errors).forEach(([field, msgs]) => {
                                formik.setFieldError(field, Array.isArray(msgs) ? msgs[0] : msgs);
                            });
                        } else {
                            window.alert('Errore di validazione. Riprovare.');
                        }
                    } else {
                        window.alert('Errore nel salvataggio. Riprovare.');
                    }
                })
                .finally(() => setLoading(false));
        },
        enableReinitialize: false,
    });

    return (
        <>
            <Card sx={{ marginTop: '20px' }}>
                <CardHeader
                    title="Tipi di bollino"
                    subheader="Gestione delle tipologie di bollini per provincia"
                    action={
                        <Button variant="contained" onClick={openCreate} sx={{ mt: 1, mr: 1 }}>
                            Nuovo tipo
                        </Button>
                    }
                />
                <CardContent sx={{ pt: 0 }}>
                    <ThemeProvider theme={theme}>
                        <Box sx={{ width: '100%' }}>
                            <DataGrid
                                rows={rows}
                                columns={columns}
                                sx={{ minHeight: '200px' }}
                                autoHeight
                                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                                pageSizeOptions={[10, 25, 50]}
                                disableRowSelectionOnClick
                            />
                        </Box>
                    </ThemeProvider>
                    {listAlert === -1 && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                            Si è verificato un errore nel caricamento dei dati.
                        </Alert>
                    )}
                    {saveAlert === 1 && (
                        <Alert severity="success" sx={{ mt: 1 }}>
                            Operazione completata con successo!
                        </Alert>
                    )}
                </CardContent>
            </Card>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingId ? 'Modifica tipo bollino' : 'Nuovo tipo bollino'}</DialogTitle>
                <form onSubmit={formik.handleSubmit} noValidate autoComplete="off">
                    <DialogContent>
                        <div className="row styled">
                            <div className="col-md-12">
                                <FastTextField
                                    loading={loading}
                                    required
                                    formik={formik}
                                    label="Provincia"
                                    field="area"
                                    select
                                    disabled={Boolean(editingId)}
                                    onChange={e => formik.setFieldValue('area', e.target.value)}
                                    child={optionsArea.map(a => (
                                        <MenuItem key={a} value={a}>{a}</MenuItem>
                                    ))}
                                />
                            </div>
                        </div>
                        <div className="row styled">
                            <div className="col-md-6">
                                <FastTextField
                                    loading={loading}
                                    required
                                    formik={formik}
                                    label="Potenza min. (kW)"
                                    field="min_power"
                                    type="number"
                                    disabled={Boolean(editingId)}
                                    inputProps={{ step: '0.01', min: '0' }}
                                />
                            </div>
                            <div className="col-md-6">
                                <FastTextField
                                    loading={loading}
                                    required
                                    formik={formik}
                                    label="Potenza max. (kW)"
                                    field="max_power"
                                    type="number"
                                    disabled={Boolean(editingId)}
                                    inputProps={{ step: '0.01', min: '0' }}
                                />
                            </div>
                        </div>
                        <div className="row styled">
                            <div className="col-md-6">
                                <FastTextField
                                    loading={loading}
                                    formik={formik}
                                    label="Prezzo (€)"
                                    field="price"
                                    type="number"
                                    inputProps={{ step: '0.01', min: '0' }}
                                />
                            </div>
                            <div className="col-md-6">
                                <FastTextField
                                    loading={loading}
                                    required
                                    formik={formik}
                                    label="Periodicità (anni)"
                                    field="periodicity_years"
                                    type="number"
                                    inputProps={{ step: '1', min: '1' }}
                                />
                            </div>
                        </div>
                        <FormikValidation
                            formikValues={formik.values}
                            formikErrors={formik.errors}
                            formikTouched={formik.touched}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDialogOpen(false)} disabled={loading}>
                            Annulla
                        </Button>
                        <Button variant="contained" type="submit" disabled={loading}>
                            {editingId ? 'Salva modifiche' : 'Crea'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </>
    );
}

export default StampTypeSection;
